import React, { useEffect, useState, useCallback, useRef } from "react";
import { Star, MapPin, Users, ChevronRight, Heart, Flame, Play, Video, Volume2, VolumeX, Eye } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { ExhibitionCountry, ExhibitionBooth } from "@/hooks/useExhibition";
import { cn } from "@/lib/utils";
import { toast } from "@/hooks/use-toast";
import { Button } from "@/components/ui/button";
import IntentIcon from "./IntentIcon";
import FavoriteButton from "./FavoriteButton";
import BoothPeekModal from "./BoothPeekModal";

// Video preview component with hover effects and audio toggle
interface VideoPreviewProps {
  booth: {
    id: string;
    name: string;
    topic: string;
    exhibition_intent: string | null;
    participants_count: number;
    max_participants: number;
    host_name: string | null;
    host_photo: string | null;
    city_name: string | null;
    country_flag: string | null;
    participant_previews: Array<{
      id: string;
      name: string;
      photo_url: string | null;
    }>;
  };
  onJoin: () => void;
}

const VideoPreviewThumbnail: React.FC<VideoPreviewProps> = ({ booth, onJoin }) => {
  const [isHovered, setIsHovered] = useState(false);
  const [isMuted, setIsMuted] = useState(true);
  const [isAudioPlaying, setIsAudioPlaying] = useState(false);
  const [showPeekModal, setShowPeekModal] = useState(false);

  const handleAudioToggle = (e: React.MouseEvent) => {
    e.stopPropagation();
    setIsMuted(!isMuted);
    
    if (isMuted) {
      // Simulate audio preview starting
      setIsAudioPlaying(true);
      toast({
        title: "🔊 Audio Preview",
        description: `Listening to "${booth.name}"... (simulated)`,
      });
      // Auto-mute after 5 seconds
      setTimeout(() => {
        setIsMuted(true);
        setIsAudioPlaying(false);
      }, 5000);
    } else {
      setIsAudioPlaying(false);
    }
  };

  const handlePeekClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    setShowPeekModal(true);
  };

  return (
    <>
      <div 
        className={cn(
          "relative w-full rounded-lg overflow-hidden bg-gradient-to-br from-gray-900 to-gray-800 mt-1 transition-all duration-300 ease-out cursor-pointer",
          isHovered ? "h-28 scale-[1.02] shadow-xl ring-2 ring-pink-500/50" : "h-20"
        )}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
      >
        {/* Mini video grid preview */}
        <div className="absolute inset-0 grid grid-cols-4 gap-0.5 p-0.5">
          {booth.participant_previews.slice(0, 4).map((participant, idx) => (
            <div 
              key={participant.id}
              className="relative bg-gradient-to-br from-gray-700 to-gray-800 rounded-sm overflow-hidden group"
            >
              {participant.photo_url ? (
                <img
                  src={participant.photo_url}
                  alt={participant.name}
                  className="w-full h-full object-cover"
                />
              ) : (
                <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-purple-500 to-pink-500">
                  <span className="text-white text-sm font-medium">
                    {participant.name.charAt(0).toUpperCase()}
                  </span>
                </div>
              )}
              {/* Simulated video activity indicator */}
              <div className={cn(
                "absolute bottom-0.5 left-0.5 w-1.5 h-1.5 rounded-full bg-green-500",
                isAudioPlaying ? "animate-ping" : "animate-pulse"
              )} />
              
              {/* Participant name on hover */}
              {isHovered && (
                <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/80 to-transparent p-1">
                  <p className="text-[9px] text-white truncate text-center font-medium">
                    {participant.name}
                  </p>
                </div>
              )}
            </div>
          ))}
          {/* Fill remaining slots with placeholder */}
          {booth.participant_previews.length < 4 && 
            Array.from({ length: 4 - booth.participant_previews.length }).map((_, idx) => (
              <div 
                key={`empty-${idx}`}
                className="bg-gray-800/50 rounded-sm flex items-center justify-center"
              >
                <Video className="w-3 h-3 text-gray-600" />
              </div>
            ))
          }
        </div>
        
        {/* Live badge overlay */}
        <div className={cn(
          "absolute top-1 left-1 flex items-center gap-1 px-1.5 py-0.5 rounded-full text-white text-[10px] font-medium transition-colors",
          isAudioPlaying ? "bg-green-500/90" : "bg-red-500/90"
        )}>
          <span className="relative flex h-1.5 w-1.5">
            <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-white opacity-75" />
            <span className="relative inline-flex rounded-full h-1.5 w-1.5 bg-white" />
          </span>
          {isAudioPlaying ? "LISTENING" : "LIVE"}
        </div>
        
        {/* Top right controls */}
        <div className="absolute top-1 right-1 flex items-center gap-1">
          {/* Peek button */}
          <button
            onClick={handlePeekClick}
            className="p-1.5 rounded-full bg-black/60 hover:bg-pink-500 transition-colors group"
            title="Peek inside"
          >
            <Eye className="w-3 h-3 text-white group-hover:scale-110 transition-transform" />
          </button>
          
          {/* Audio toggle button */}
          <button
            onClick={handleAudioToggle}
            className={cn(
              "p-1.5 rounded-full transition-all",
              isMuted 
                ? "bg-black/60 hover:bg-black/80" 
                : "bg-green-500 hover:bg-green-600 animate-pulse"
            )}
          >
            {isMuted ? (
              <VolumeX className="w-3 h-3 text-white" />
            ) : (
              <Volume2 className="w-3 h-3 text-white" />
            )}
          </button>
        </div>
        
        {/* Participant count overlay */}
        {booth.participants_count > 4 && (
          <div className="absolute bottom-1 left-1 px-1.5 py-0.5 rounded-full bg-black/60 text-white text-[10px] font-medium">
            +{booth.participants_count - 4} more
          </div>
        )}
        
        {/* Video icon indicator */}
        <div className="absolute bottom-1 right-1 p-1 rounded-full bg-black/40">
          <Video className="w-3 h-3 text-white" />
        </div>
        
        {/* Hover hint */}
        {isHovered && (
          <div className="absolute inset-x-0 top-1/2 -translate-y-1/2 flex justify-center pointer-events-none">
            <div className="px-2 py-1 rounded-full bg-black/70 text-white text-[10px] font-medium backdrop-blur-sm flex items-center gap-1.5">
              <Eye className="w-3 h-3" />
              Peek inside
            </div>
          </div>
        )}
      </div>

      {/* Peek Modal */}
      <BoothPeekModal
        isOpen={showPeekModal}
        onClose={() => setShowPeekModal(false)}
        onJoin={onJoin}
        booth={booth}
      />
    </>
  );
};

interface ExhibitionFavoritesTabProps {
  favoriteCountryCodes: string[];
  favoriteBoothIds: string[];
  countries: ExhibitionCountry[];
  onSelectCountry: (country: ExhibitionCountry) => void;
  onPreviewBooth: (boothId: string) => void;
  onJoinBooth: (boothId: string) => void;
  onToggleCountryFavorite: (code: string) => void;
  onToggleBoothFavorite: (id: string) => void;
}

interface ParticipantPreview {
  id: string;
  name: string;
  photo_url: string | null;
}

interface FavoriteBoothData {
  id: string;
  name: string;
  topic: string;
  exhibition_intent: string | null;
  is_active: boolean;
  is_video_enabled: boolean;
  participants_count: number;
  max_participants: number;
  host_name: string | null;
  host_photo: string | null;
  city_name: string | null;
  country_name: string | null;
  country_flag: string | null;
  participant_previews: ParticipantPreview[];
}

const ExhibitionFavoritesTab = React.forwardRef<HTMLDivElement, ExhibitionFavoritesTabProps>((
  {
    favoriteCountryCodes,
    favoriteBoothIds,
    countries,
    onSelectCountry,
    onPreviewBooth,
    onJoinBooth,
    onToggleCountryFavorite,
    onToggleBoothFavorite,
  },
  ref
) => {
  const [favoriteBooths, setFavoriteBooths] = useState<FavoriteBoothData[]>([]);
  const [loading, setLoading] = useState(false);
  const previousBoothsRef = useRef<Map<string, boolean>>(new Map());

  // Fetch favorite booth details
  const fetchFavoriteBooths = useCallback(async () => {
    if (favoriteBoothIds.length === 0) {
      setFavoriteBooths([]);
      return;
    }

    setLoading(true);
    try {
      const { data, error } = await supabase
        .from("live_arenas")
        .select(`
          id,
          name,
          topic,
          exhibition_intent,
          is_active,
          is_video_enabled,
          max_participants,
          profiles:created_by (name, photo_url),
          cities:city_id (name, countries:country_code (name, flag))
        `)
        .in("id", favoriteBoothIds)
        .eq("is_exhibition_booth", true);

      if (error) throw error;

      // Get participant counts and preview avatars
      const boothsWithCounts = await Promise.all(
        (data || []).map(async (booth) => {
          // Get participant count
          const { count } = await supabase
            .from("arena_participants")
            .select("*", { count: "exact", head: true })
            .eq("arena_id", booth.id)
            .is("left_at", null);

          // Get up to 4 participant avatars for preview
          const { data: participantData } = await supabase
            .from("arena_participants")
            .select("user_id, profiles:user_id (id, name, photo_url)")
            .eq("arena_id", booth.id)
            .is("left_at", null)
            .limit(4);

          const participantPreviews: ParticipantPreview[] = (participantData || []).map((p: any) => ({
            id: p.profiles?.id || p.user_id,
            name: p.profiles?.name || "User",
            photo_url: p.profiles?.photo_url,
          }));

          const host = booth.profiles as any;
          const city = booth.cities as any;
          const country = city?.countries as any;

          return {
            id: booth.id,
            name: booth.name,
            topic: booth.topic,
            exhibition_intent: booth.exhibition_intent,
            is_active: booth.is_active,
            is_video_enabled: booth.is_video_enabled ?? false,
            participants_count: count || 0,
            max_participants: booth.max_participants,
            host_name: host?.name,
            host_photo: host?.photo_url,
            city_name: city?.name,
            country_name: country?.name,
            country_flag: country?.flag,
            participant_previews: participantPreviews,
          };
        })
      );

      setFavoriteBooths(boothsWithCounts);
    } catch (error) {
      console.error("Error fetching favorite booths:", error);
    } finally {
      setLoading(false);
    }
  }, [favoriteBoothIds]);

  // Initial fetch
  useEffect(() => {
    fetchFavoriteBooths();
  }, [fetchFavoriteBooths]);

  // Track previous booth active states for toast notifications
  useEffect(() => {
    favoriteBooths.forEach((booth) => {
      previousBoothsRef.current.set(booth.id, booth.is_active);
    });
  }, [favoriteBooths]);

  // Real-time subscription for booth active status changes
  useEffect(() => {
    if (favoriteBoothIds.length === 0) return;

    const channel = supabase
      .channel('favorite-booths-status')
      .on(
        'postgres_changes',
        {
          event: 'UPDATE',
          schema: 'public',
          table: 'live_arenas',
          filter: `id=in.(${favoriteBoothIds.join(',')})`,
        },
        (payload) => {
          console.log('Booth status update:', payload);
          const updatedBooth = payload.new as any;
          const wasActive = previousBoothsRef.current.get(updatedBooth.id);
          
          // Show toast if booth just became active
          if (!wasActive && updatedBooth.is_active) {
            const boothData = favoriteBooths.find(b => b.id === updatedBooth.id);
            toast({
              title: "🎉 Favorite Booth Now Live!",
              description: boothData 
                ? `"${boothData.name}" is now active. Join now!`
                : "One of your favorite booths is now active!",
              action: (
                <Button 
                  size="sm" 
                  onClick={() => onJoinBooth(updatedBooth.id)}
                  className="bg-gradient-to-r from-pink-500 to-rose-500 text-white"
                >
                  Join
                </Button>
              ),
            });
          }
          
          // Update the ref with new state
          previousBoothsRef.current.set(updatedBooth.id, updatedBooth.is_active);
          
          setFavoriteBooths((prev) =>
            prev.map((booth) =>
              booth.id === updatedBooth.id
                ? { ...booth, is_active: updatedBooth.is_active }
                : booth
            )
          );
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [favoriteBoothIds, favoriteBooths, onJoinBooth]);

  // Real-time subscription for participant changes
  useEffect(() => {
    if (favoriteBoothIds.length === 0) return;

    const channel = supabase
      .channel('favorite-booths-participants')
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'arena_participants',
        },
        async (payload) => {
          const arenaId = (payload.new as any)?.arena_id || (payload.old as any)?.arena_id;
          
          // Only process if it's a favorited booth
          if (!favoriteBoothIds.includes(arenaId)) return;
          
          console.log('Participant change for booth:', arenaId);

          // Fetch updated count for this specific booth
          const { count } = await supabase
            .from("arena_participants")
            .select("*", { count: "exact", head: true })
            .eq("arena_id", arenaId)
            .is("left_at", null);

          setFavoriteBooths((prev) =>
            prev.map((booth) =>
              booth.id === arenaId
                ? { ...booth, participants_count: count || 0 }
                : booth
            )
          );
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [favoriteBoothIds]);

  const favoriteCountries = countries.filter((c) =>
    favoriteCountryCodes.includes(c.code)
  );

  const hasNoFavorites =
    favoriteCountryCodes.length === 0 && favoriteBoothIds.length === 0;

  if (hasNoFavorites) {
    return (
      <div ref={ref} className="mx-4 mt-4 p-8 rounded-2xl backdrop-blur-xl bg-white/50 border border-white/60 text-center">
        <Star className="w-12 h-12 mx-auto text-muted-foreground mb-4" />
        <h3 className="font-semibold text-foreground mb-2">No Favorites Yet</h3>
        <p className="text-sm text-muted-foreground max-w-xs mx-auto">
          Star countries and booths to quickly access them here. You'll also get
          notifications when your favorite booths become active!
        </p>
      </div>
    );
  }

  return (
    <div ref={ref} className="mx-4 mt-4 space-y-4">
      {/* Favorite Countries */}
      {favoriteCountries.length > 0 && (
        <div className="rounded-2xl backdrop-blur-xl bg-white/50 border border-white/60 p-4">
          <div className="flex items-center gap-2 mb-3">
            <MapPin className="w-4 h-4 text-amber-500" />
            <span className="font-semibold text-foreground">
              Favorite Countries
            </span>
            <span className="text-xs text-muted-foreground">
              ({favoriteCountries.length})
            </span>
          </div>

          <div className="space-y-2">
            {favoriteCountries.map((country) => (
              <button
                key={country.code}
                onClick={() => onSelectCountry(country)}
                className="w-full flex items-center gap-3 p-3 rounded-xl bg-white/60 hover:bg-white/80 transition-all text-left"
              >
                <span className="text-2xl">{country.flag}</span>
                <div className="flex-1 min-w-0">
                  <p className="font-medium text-foreground truncate">
                    {country.name}
                  </p>
                  <p className="text-xs text-muted-foreground">
                    {country.cityCount} Cities • {country.boothCount} Booths
                  </p>
                </div>
                {country.participantCount > 0 && (
                  <div className="flex items-center gap-1">
                    <span className="relative flex h-2 w-2">
                      <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-green-500 opacity-75" />
                      <span className="relative inline-flex rounded-full h-2 w-2 bg-green-500" />
                    </span>
                    <span className="text-sm font-medium text-green-600">
                      {country.participantCount}
                    </span>
                  </div>
                )}
                <FavoriteButton
                  isFavorite={true}
                  onClick={(e) => {
                    e.stopPropagation();
                    onToggleCountryFavorite(country.code);
                  }}
                  size="sm"
                />
                <ChevronRight className="w-4 h-4 text-muted-foreground" />
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Favorite Booths */}
      {favoriteBoothIds.length > 0 && (
        <div className="rounded-2xl backdrop-blur-xl bg-white/50 border border-white/60 p-4">
          <div className="flex items-center gap-2 mb-3">
            <Heart className="w-4 h-4 text-amber-500" />
            <span className="font-semibold text-foreground">
              Favorite Booths
            </span>
            <span className="text-xs text-muted-foreground">
              ({favoriteBoothIds.length})
            </span>
          </div>

          {loading ? (
            <div className="flex justify-center py-6">
              <div className="w-6 h-6 border-2 border-pop border-t-transparent rounded-full animate-spin" />
            </div>
          ) : (
            <div className="space-y-2">
              {favoriteBooths.map((booth) => {
                const isHot =
                  booth.participants_count >= booth.max_participants * 0.7;

                  return (
                    <button
                      key={booth.id}
                      onClick={() => onPreviewBooth(booth.id)}
                      className={cn(
                        "w-full flex flex-col gap-2 p-3 rounded-xl bg-white/60 hover:bg-white/80 transition-all text-left",
                        !booth.is_active && "opacity-60"
                      )}
                    >
                      {/* Top row: Host photo, name, intent, controls */}
                      <div className="flex items-center gap-3 w-full">
                        {booth.host_photo ? (
                          <img
                            src={booth.host_photo}
                            alt={booth.host_name || "Host"}
                            className="w-10 h-10 rounded-full object-cover ring-2 ring-white shadow-sm"
                          />
                        ) : (
                          <div className="w-10 h-10 rounded-full bg-gradient-to-br from-pink-400 to-rose-500 flex items-center justify-center ring-2 ring-white shadow-sm">
                            <Heart className="w-5 h-5 text-white" />
                          </div>
                        )}

                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2">
                            <p className="font-medium text-foreground truncate">
                              {booth.name}
                            </p>
                            {booth.exhibition_intent && (
                              <IntentIcon
                                intent={booth.exhibition_intent}
                                size="sm"
                                showLabel={false}
                              />
                            )}
                          </div>
                          <p className="text-xs text-muted-foreground truncate">
                            {booth.city_name && booth.country_flag
                              ? `${booth.country_flag} ${booth.city_name}`
                              : booth.topic}
                          </p>
                        </div>

                        {booth.is_active ? (
                          <div className="flex items-center gap-2">
                            {isHot && (
                              <Flame className="w-4 h-4 text-amber-500 animate-pulse" />
                            )}
                            <Button
                              size="sm"
                              onClick={(e) => {
                                e.stopPropagation();
                                onJoinBooth(booth.id);
                              }}
                              className="h-7 px-2.5 bg-gradient-to-r from-pink-500 to-rose-500 hover:from-pink-600 hover:to-rose-600 text-white text-xs font-medium"
                            >
                              <Play className="w-3 h-3 mr-1" />
                              Join
                            </Button>
                          </div>
                        ) : (
                          <span className="text-xs px-2 py-1 rounded-full bg-muted text-muted-foreground">
                            Offline
                          </span>
                        )}

                        <FavoriteButton
                          isFavorite={true}
                          onClick={(e) => {
                            e.stopPropagation();
                            onToggleBoothFavorite(booth.id);
                          }}
                          size="sm"
                        />
                      </div>

                      {/* Video preview for active video-enabled booths */}
                      {booth.is_active && booth.is_video_enabled && booth.participant_previews.length > 0 && (
                        <VideoPreviewThumbnail booth={booth} onJoin={() => onJoinBooth(booth.id)} />
                      )}

                      {/* Participant preview row - only for active NON-video booths with participants */}
                      {booth.is_active && !booth.is_video_enabled && booth.participant_previews.length > 0 && (
                        <div className="flex items-center gap-2 pl-1">
                          <div className="flex -space-x-2">
                            {booth.participant_previews.slice(0, 4).map((participant, idx) => (
                              <div
                                key={participant.id}
                                className="relative"
                                style={{ zIndex: 4 - idx }}
                              >
                                {participant.photo_url ? (
                                  <img
                                    src={participant.photo_url}
                                    alt={participant.name}
                                    className="w-7 h-7 rounded-full object-cover ring-2 ring-white shadow-sm"
                                    title={participant.name}
                                  />
                                ) : (
                                  <div 
                                    className="w-7 h-7 rounded-full bg-gradient-to-br from-purple-400 to-pink-400 flex items-center justify-center ring-2 ring-white shadow-sm text-white text-xs font-medium"
                                    title={participant.name}
                                  >
                                    {participant.name.charAt(0).toUpperCase()}
                                  </div>
                                )}
                              </div>
                            ))}
                            {booth.participants_count > 4 && (
                              <div className="w-7 h-7 rounded-full bg-muted flex items-center justify-center ring-2 ring-white shadow-sm text-xs font-medium text-muted-foreground">
                                +{booth.participants_count - 4}
                              </div>
                            )}
                          </div>
                          <span className="text-xs text-muted-foreground">
                            {booth.participants_count} {booth.participants_count === 1 ? 'person' : 'people'} inside
                          </span>
                        </div>
                      )}
                    </button>
                );
              })}
            </div>
          )}
        </div>
      )}
    </div>
  );
});

ExhibitionFavoritesTab.displayName = "ExhibitionFavoritesTab";

export default ExhibitionFavoritesTab;
